class CollectionFilterPriceRange extends HTMLElement {
  static observedAttributes = [
    "filter-name",
    "filter-min",
    "filter-max",
    "filter-status-min",
    "filter-status-max"
  ];

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });

    this.state = { 
      from: parseFloat(this.filterStatusMin),
	    to: parseFloat(this.filterStatusMax),
	    min: parseFloat(this.filterMin),
	    max: parseFloat(this.filterMax),
    }

    this.render();
  }

  get filterName() {
    return this.getAttribute('filter-name') || 'Price($)';
  }

  get filterMin() {
    return this.getAttribute('filter-min') || 0;
  }

  get filterMax() {
    return this.getAttribute('filter-max') || 1000;
  }

  get filterStatusMin() {
    return this.getAttribute('filter-status-min') || this.filterMin;
  }

  get filterStatusMax() {
    return this.getAttribute('filter-status-max') || this.filterMax;
  }

  get isFiltered() {
    return this.getAttribute('is-filtered') === 'true';
  }

  set isFiltered(value) {
    this.setAttribute('is-filtered', value ? 'true' : 'false');
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === null) return;

    if (name === 'filter-min' || name === 'filter-max') {
      const { from, to } = this.state;
      
      if (this.isFiltered) { 
        if (name === 'filter-min' && from > parseFloat(newValue)) {
          this.fromSliderEl.min = newValue;
          this.toSliderEl.min = newValue;
          this.state.min = parseFloat(newValue);
        } else if (name === 'filter-max' && to < parseFloat(newValue)) {
          this.fromSliderEl.max = newValue;
          this.toSliderEl.max = newValue;
          this.state.max = parseFloat(newValue);
        }
      } else {
        if (name === 'filter-min') {
          this.fromSliderEl.min = newValue;
          this.fromSliderEl.value = newValue;
          this.toSliderEl.min = newValue;
          this.state.min = parseFloat(newValue);
          this.state.from = parseFloat(newValue);
        } else if (name === 'filter-max') {
          this.toSliderEl.max = newValue;
          this.toSliderEl.value = newValue;
          this.fromSliderEl.max = newValue;
          this.state.max = parseFloat(newValue);
          this.state.to = parseFloat(newValue);
        }
      }
      this.updateUI();
    } else if (oldValue !== newValue) {
      this.updateState();
    }
  }

  updateState() { 
    this.isFiltered = true;
    let fromValue = parseFloat(this.fromSliderEl.value);
    let toValue = parseFloat(this.toSliderEl.value);

    if (fromValue > toValue) {
      [fromValue, toValue] = [toValue, fromValue];
    }

    this.state = { 
      ...this.state,
      from: fromValue,
      to: toValue,
    }

    this.updateRange();
  }

  updateRange() {
    this.updateUI();
    this.debouncedCallApi();
  }

  updateUI() {
    if (!this.fromSliderEl || !this.toSliderEl || !this.sliderRangeEl) return;

    const { from, to, min, max } = this.state;

    this.fromValueEl.textContent = from;
    this.toValueEl.textContent = to;

    let percentFrom = ((from - min) / (max - min)) * 100;
    let percentTo = ((to - min) / (max - min)) * 100;

    if (min === max) { 
      percentFrom = 0;
      percentTo = 100;

      this.toSliderEl.style.transform = 'translateX(93.2%)';
    } else { 
      this.toSliderEl.style.transform = 'translateX(0)';
    }

    this.sliderRangeEl.style.left = `${percentFrom}%`;
    this.sliderRangeEl.style.width = `${percentTo - percentFrom}%`;
  }

  addEventListeners() {
    this.debouncedCallApi = this.debounce(() => {
      const { from, to } = this.state;

      const constructorCollectionPage = document.querySelector('constructor-collection-page');
      if (constructorCollectionPage) {
        constructorCollectionPage.filterFromPrice = from;
        constructorCollectionPage.filterToPrice = to;

        constructorCollectionPage.handlePriceFilterChange();
      } else {
        console.warn('constructor-collection-page not found');
      }

    }, 200);

    this.fromSliderEl.addEventListener('input', () => this.updateState());
    this.toSliderEl.addEventListener('input', () => this.updateState());
  }

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  connectedCallback() {
    this.fromSliderEl = this.shadowRoot.querySelector('#fromSlider');
    this.toSliderEl = this.shadowRoot.querySelector('#toSlider');
    this.sliderRangeEl = this.shadowRoot.querySelector('#slider-range');
    this.fromValueEl = this.shadowRoot.querySelector('#fromValue');
    this.toValueEl = this.shadowRoot.querySelector('#toValue');

    this.updateUI();
    this.addEventListeners();
  }

  render() {
    const { from, to, min, max } = this.state;

    const template = `
      <div class="mobile-facets__details js-filter" data-index="mobile-1">
        <div
          class="mobile-facets__summary focus-inset"
          role="button"
          aria-expanded="false"
        >
          <span>
            ${this.filterName}
          </span>
        </div>

        <div class="range-slider__wrapper">
          <span class="range-slider__value range-slider__from" id="fromValue">
            ${min}
          </span>

          <div class="range-slider">
            <div class="slider-track"></div>
            <div class="slider-range" id="slider-range"></div>

            <input
              type="range"
              id="fromSlider"
              min="${min}"
              max="${max}"
              value="${from}"
              name="constructor-range-facet__price-from"
            />

            <input
              type="range"
              id="toSlider"
              min="${min}"
              max="${max}"
              value="${to}"
              name="constructor-range-facet__price-to"
            />
          </div>

          <span class="range-slider__value range-slider__to" id="toValue">
            ${max}
          </span>
        </div>
      </div>
    `;
    this.shadowRoot.innerHTML = template;
    this.appendChildStyle();
  }

  appendChildStyle() {
    const style = document.createElement('style');
    style.textContent = `
      .mobile-facets__summary { 
        font-size: 15px;
        font-weight: 600;
      }
        
      .price-range {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      .range-slider__wrapper {
        display: grid;
        grid-template-columns: minmax(0, 25px) 1fr 25px;
        align-items: center;
        width: 100%;
      }

      .range-slider {
        width: 100%;
        position: relative;
      }

      .range-slider input[type="range"] {
        position: absolute;
        left: 0;
        top: -8px;
        width: 100%;
        pointer-events: none;
        -webkit-appearance: none;
        background: transparent;
        margin: 0;
      }

      .range-slider input[type="range"]:first-of-type {
        left: 0;
        right: auto;
      }

      .range-slider input[type="range"]:last-of-type {
        left: auto;
        right: 0;
      }

      .range-slider input[type="range"]::-webkit-slider-thumb {
        pointer-events: all;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: white;
        border: 2px solid #ce2333;
        cursor: pointer;
        -webkit-appearance: none;
      }

      .slider-track {
        position: absolute;
        height: 4px;
        background: #F5F5F5;
        width: 100%;
        border-radius: 4px;
        top: -2px;
      }

      .slider-range {
        position: absolute;
        height: 4px;
        background: #ce2333;
        border-radius: 4px;
        top: -2px;
      }

      .range-slider__value {
        font-size: 15px;
        font-weight: 600;
        color: #333;
        display: flex;
        justify-content: center;
      }

      #toValue { 
        margin-left: 14px;
      }
    `;
    this.shadowRoot.appendChild(style);
  }
}

customElements.define('collection-filter-price-range', CollectionFilterPriceRange);
